import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';

import { LoginService } from '../services/login.service';
import { LoginRequest } from '../models/login-request.model';

import { AuthService } from '../../../core/services/auth.service';
import { ThemeService } from '../../../core/services/theme.service';
import { AppSettingsService } from '../../../core/services/app-settings.service';
import {
  switchMap,
  timeout,
  TimeoutError,
} from 'rxjs';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule,
    RouterLink,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly loginService = inject(LoginService);
  private readonly authService = inject(AuthService);

  protected readonly themeService = inject(ThemeService);

  protected readonly hidePassword = signal(true);
  protected readonly isSubmitting = signal(false);
  protected readonly serverError = signal<string | null>(null);
  private readonly appSettingsService = inject(AppSettingsService);
  protected readonly loginForm = this.fb.nonNullable.group({
    username: ['', [Validators.required]],
    password: ['', [Validators.required]],
  });
  protected readonly backendVersion = signal<string | null>(null);

  constructor() {
    this.themeService.forceLightTheme();
    this.loadBackendVersion();

    if (this.authService.hasValidSession()) {
      if (this.authService.mustChangePassword()) {
        this.router.navigate(['/change-password']);
        return;
      }

      this.router.navigate([this.authService.getFirstAllowedRoute()]);
    }
  }

  protected togglePasswordVisibility(): void {
    this.hidePassword.update((value) => !value);
  }

  protected submit(): void {
    this.serverError.set(null);

    const usernameInput = document.getElementById('username') as HTMLInputElement | null;

    if (usernameInput) {
      const value = usernameInput.value.trim();

      if (!value) {
        usernameInput.setCustomValidity('Fülle dieses Feld aus.');
      } else if (this.isValidUsername(value) || this.isValidEmail(value)) {
        usernameInput.setCustomValidity('');
      } else {
        usernameInput.setCustomValidity(
          'Bitte geben Sie eine gültige E-Mail oder einen bekannten Benutzernamen ein.'
        );
      }

      if (!usernameInput.checkValidity()) {
        usernameInput.reportValidity();
        return;
      }
    }

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    const payload: LoginRequest = {
      usernameOrEmail: this.loginForm.controls.username.value.trim(),
      password: this.loginForm.controls.password.value,
    };

    this.loginService
      .login(payload)
      .pipe(
        switchMap((response) => {
          /*
           * The token is required for /me/modules,
           * /me/filialen and /me/roles.
           * AuthService keeps the session in a
           * "not ready" state until these requests succeed.
           */
          this.authService.login(response);

          if (
            this.authService.mustChangePassword()
          ) {
            return [null];
          }

          return this.authService
            .hydrateRuntimePermissions();
        }),

        /*
         * Prevent an endless spinner when the backend
         * is completely unavailable.
         */
        timeout({
          first: 20_000,
        })
      )
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);

          if (
            this.authService.mustChangePassword()
          ) {
            this.router.navigate([
              '/change-password',
            ]);

            return;
          }

          this.router.navigate([
            this.authService
              .getFirstAllowedRoute(),
          ]);
        },

        error: (error: unknown) => {
          /*
           * Important:
           * Never keep tokens from a partially completed
           * login attempt.
           */
          this.authService.clearSession();

          this.isSubmitting.set(false);

          if (error instanceof TimeoutError) {
            this.serverError.set(
              'Der Server antwortet derzeit nicht. Bitte versuchen Sie es später erneut.'
            );

            return;
          }

          this.serverError.set(
            'Die Anmeldung ist fehlgeschlagen. Bitte überprüfen Sie Ihre Eingaben.'
          );
        },
      });
  }

  protected onUsernameInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    input.setCustomValidity('');

    if (!value) {
      return;
    }

    if (this.isValidUsername(value) || this.isValidEmail(value)) {
      input.setCustomValidity('');
      return;
    }

    input.setCustomValidity(
      'Bitte geben Sie eine gültige E-Mail oder einen bekannten Benutzernamen ein.'
    );
  }

  protected onUsernameInvalid(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    if (!value) {
      input.setCustomValidity('Fülle dieses Feld aus.');
      return;
    }

    if (this.isValidUsername(value) || this.isValidEmail(value)) {
      input.setCustomValidity('');
      return;
    }

    input.setCustomValidity(
      'Bitte geben Sie eine gültige E-Mail oder einen bekannten Benutzernamen ein.'
    );
  }

  private isValidEmail(value: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    return emailRegex.test(value);
  }

  private isValidUsername(value: string): boolean {
    const usernameRegex = /^[a-zA-Z0-9._-]{3,}$/;

    return usernameRegex.test(value);
  }

  private loadBackendVersion(): void {
    this.loginService.getBackendVersion().subscribe((response) => {
      if (response?.version) {
        this.backendVersion.set(response.version);
      }
    });
  }
}